import styled, { css } from 'styled-components';

import {
    spacing,
    colors,
    transparencies,
    shadows,
    Fonts,
    Spacing,
    ColorsTransparencies,
    mobileCssQuerist,
} from 'styles/theme';

export const MainViewWrapper = styled.div`
    height: 100%;
    display: flex;
    overflow: hidden;

    ${mobileCssQuerist(css`
        flex-direction: column-reverse;
        overflow: scroll;
    `)}
`;

export const ContentScroller = styled.div`
    width: 650px;
    max-height: 100vh;
    overflow: scroll;

    ${mobileCssQuerist(css`
        max-height: none;
        width: 100%;
    `)}
`;

export const FullBleedScroller = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: scroll;

    ${mobileCssQuerist(
        css`
            height: auto;
            overflow-x: hidden;
        `
    )}
`;

interface CustomizableWidthWrapper {
    $width: string;
}

export const CustomizableWidthWrapper = styled.div<CustomizableWidthWrapper>`
    ${(props) => css`
        width: ${props.$width};
    `};
`;

interface HorizontalFlexWrapperProps {
    $isWrapping?: boolean;
    $isMarginAuto?: boolean;
    $isContentSpread?: boolean;
    $isContentSpaced?: boolean;
    $isVerticalOnMobile?: boolean;
    $isJustifyContentCenter?: boolean;
    $isAlignItemsStart?: boolean;
}

export const HorizontalFlexWrapper = styled.div<HorizontalFlexWrapperProps>`
    display: flex;
    align-items: center;

    ${(props) =>
        props.$isWrapping
            ? css`
                  flex-wrap: wrap;
              `
            : null}

    ${(props) =>
        props.$isMarginAuto
            ? css`
                  margin: auto;
              `
            : null}

    ${(props) =>
        props.$isContentSpread
            ? css`
                  justify-content: space-between;
              `
            : null}

    ${(props) =>
        props.$isContentSpaced
            ? css`
                  justify-content: space-around;
              `
            : null}

    ${(props) =>
        props.$isVerticalOnMobile
            ? mobileCssQuerist(css`
                  flex-direction: column;
                  align-items: center;
              `)
            : null}

    ${({ $isJustifyContentCenter }) =>
        $isJustifyContentCenter &&
        css`
            justify-content: center;
        `}

    ${({ $isAlignItemsStart }) =>
        $isAlignItemsStart &&
        css`
            align-items: start;
        `}
`;

interface CardProps {
    $width?: string;
    $mobileWidth?: string;
    $margin?: string;
    $padding?: Spacing;
    $backgroundColor?: ColorsTransparencies;
    $isMobileOverflowHidden?: boolean;
    $isMobilePaddingOverriden?: boolean;
}

export const Card = styled.div<CardProps>`
    position: relative;
    box-shadow: ${shadows.smallBase};
    border-radius: ${spacing.radiusLarge};
    border: 1px solid ${transparencies.darkJungle5};
    background-color: ${colors.white};
    padding: ${spacing.xLarge};
    display: flex;
    flex-direction: column;
    cursor: default;

    ${mobileCssQuerist(css`
        width: auto;
        margin: 0;
        padding: ${spacing.xLarge} ${spacing.medium} ${spacing.medium};
        overflow-x: hidden;
        overflow-y: visible;
    `)}

    ${(props) =>
        props.$margin == null
            ? null
            : css`
                  margin: ${props.$margin};
              `}

    ${(props) =>
        props.$width == null
            ? null
            : css`
                  width: ${props.$width};
              `}

    ${(props) =>
        props.$padding == null
            ? null
            : css`
                  padding: ${props.$padding};

                  ${mobileCssQuerist(css`
                      padding: ${props.$padding};
                  `)}
              `}

    ${({ $backgroundColor }) =>
        $backgroundColor &&
        css`
            background-color: ${$backgroundColor};
        `}

    ${(props) =>
        props.$isMobileOverflowHidden === true
            ? mobileCssQuerist(css`
                  overflow-y: hidden;
              `)
            : null}

    ${({ $isMobilePaddingOverriden }) =>
        $isMobilePaddingOverriden &&
        mobileCssQuerist(css`
            padding: ${spacing.medium} ${spacing.medium} ${spacing.large};
        `)}

    ${({ $mobileWidth }) =>
        $mobileWidth &&
        mobileCssQuerist(css`
            width: ${$mobileWidth};
        `)}
`;

export const BackButtonWrapper = styled.div`
    position: absolute;
    left: ${spacing.large};
    top: ${spacing.large};
`;

interface ProgressBarWrapperProps {
    $color?: ColorsTransparencies;
    $isStretch?: boolean;
}

export const ProgressBarWrapper = styled.div<ProgressBarWrapperProps>`
    margin: ${spacing.xxxSmall} ${spacing.xxxLarge} 0 ${spacing.xxLarge};
    border-radius: ${spacing.radiusXSmall};
    background-color: ${transparencies.darkJungle40};
    height: ${spacing.xxxSmall};
    display: flex;
    align-items: center;

    ${(props) =>
        props.$color == null
            ? null
            : css`
                  background-color: ${props.$color};
              `}

    ${(props) =>
        props.$isStretch
            ? css`
                  width: 100%;
                  margin: 0 ${spacing.small} 0 0;
              `
            : null}
`;

interface ProgressBarProps {
    $color?: ColorsTransparencies;
    $width: number;
}

export const ProgressBar = styled.div<ProgressBarProps>`
    border-radius: ${spacing.radiusXSmall};
    background-color: ${colors.grandGold};
    height: 100%;
    transition: width 0.4s;

    ${(props) =>
        props.$color == null
            ? null
            : css`
                  background-color: ${props.$color};
              `}

    ${(props) =>
        css`
            width: ${props.$width}%;
        `}
`;

export interface NestedCardProps {
    $padding?: Spacing;
}

export const NestedCard = styled.div<NestedCardProps>`
    position: relative;
    border: 1px solid ${transparencies.darkJungle5};
    border-radius: ${spacing.radiusSmall};
    background-color: ${transparencies.darkJungle5};
    padding: ${spacing.small};
    display: flex;
    flex-direction: column;

    ${(props) =>
        props.$padding == null
            ? null
            : css`
                  padding: ${props.$padding};
              `};
`;

interface CardBottomButtonWrapperProps {
    $backgroundColor?: 'white' | 'light' | 'transparent';
    $shouldCenterOnMobile?: boolean;
}

export const CardBottomButtonWrapper = styled.div<CardBottomButtonWrapperProps>`
    margin: 0 -${spacing.xLarge} -${spacing.xLarge};
    border-bottom-left-radius: ${spacing.radiusMedium};
    border-bottom-right-radius: ${spacing.radiusMedium};
    background-color: ${colors.light};
    width: calc(100% + ${spacing.xLarge} + ${spacing.xLarge});
    padding: ${spacing.xLarge};
    display: flex;
    justify-content: flex-end;

    ${(props) => {
        switch (props.$backgroundColor) {
            case 'white': {
                return css`
                    background-color: ${colors.white};
                    padding-top: 0;
                `;
            }
            case 'transparent': {
                return css`
                    background-color: transparent;
                    padding-top: 0;
                `;
            }
            case 'light':
            default: {
                return;
            }
        }
    }};

    ${({ $shouldCenterOnMobile }) =>
        $shouldCenterOnMobile &&
        mobileCssQuerist(
            css`
                justify-content: center;
            `
        )}
`;

interface BannerWrapperProps {
    $backgroundImageUrl: string;
}

export const BannerWrapper = styled.div<BannerWrapperProps>`
    // When using a background image, adding a transparent border will render
    // transparency over the edge the image regardless of how it's positioned
    // because borders exist outside of padding
    //
    // Use an insetted box shadows instead, which is rendered inside of padding
    box-shadow: inset 0 0 0 1px ${transparencies.darkJungle5};
    border-radius: ${spacing.radiusSmall};
    padding: ${spacing.large};

    ${(props) => css`
        background-image: url(${props.$backgroundImageUrl});
        background-position: top right;
        background-size: cover;
    `}
`;

interface GridListWrapperProps {
    $gap?: Spacing;
}

export const GridListWrapper = styled.div<GridListWrapperProps>`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    ${({ $gap }: GridListWrapperProps) => css`
        gap: ${$gap};
    `};
`;

interface BumperProps {
    $minHeight?: Spacing;
    $minWidth?: Spacing;
    $shouldHideOnDesktop?: boolean;
    $shouldHideOnMobile?: boolean;
}

export const Bumper = styled.div<BumperProps>`
    min-height: ${spacing.large};

    ${(props) =>
        props.$minHeight == null
            ? null
            : css`
                  min-height: ${props.$minHeight};
              `}

    ${(props) =>
        props.$minWidth == null
            ? null
            : css`
                  min-width: ${props.$minWidth};
              `}

    ${(props) =>
        props.$shouldHideOnDesktop
            ? css`
                  display: none;

                  ${mobileCssQuerist(
                      css`
                          display: block;
                      `
                  )}
              `
            : null}

    ${(props) =>
        props.$shouldHideOnMobile
            ? mobileCssQuerist(
                  css`
                      display: none;
                  `
              )
            : null}
`;

interface DividerProps {
    $backgroundColor?: ColorsTransparencies;
}

export const Divider = styled.div<DividerProps>`
    margin: ${spacing.small} 0;
    background-color: ${transparencies.darkJungle5};
    width: 100%;
    height: 1px;

    ${(props) =>
        props.$backgroundColor == null
            ? null
            : css`
                  background-color: ${props.$backgroundColor};
              `};
`;

export const Label = styled(Fonts.Small)`
    margin-bottom: ${spacing.xxSmall};
`;

export const SubLabel = styled(Fonts.XSmall)`
    margin-bottom: ${spacing.xxSmall};
    color: ${colors.grandGreen};
`;

export const CheckboxWrapper = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

export const Checkbox = styled.input`
    margin-right: ${spacing.xSmall};
    min-width: ${spacing.small};
    outline: none;
    min-height: ${spacing.small};
    cursor: pointer;

    &:checked {
        accent-color: ${colors.grandGreen};
    }
`;

interface RadioButtonProps {
    $isSelected: boolean;
}

export const RadioButton = styled.button.attrs({
    type: 'button',
})<RadioButtonProps>`
    margin-right: ${spacing.medium};
    border: 1px solid
        ${({ $isSelected }) => ($isSelected ? colors.grey40 : colors.grey20)};
    border-radius: ${spacing.large};
    background-color: 'transparent';
    width: ${spacing.xLarge};
    height: ${spacing.xLarge};
    transition: 0.2s;

    &:hover {
        background-color: ${colors.grey10};
    }
`;

export const RadioLoaderWrapper = styled.div`
    position: relative;
    top: -7px;
    left: -11px;
`;

interface RadioSelectIconProps extends RadioButtonProps {
    $imageUrl: string;
}

export const RadioSelectIcon = styled.div<RadioSelectIconProps>`
    position: relative;
    left: -1px;
    background-image: ${({ $isSelected, $imageUrl }) =>
        $isSelected ? css`url(${$imageUrl})` : ''};
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    width: ${spacing.medium};
    height: ${spacing.medium};
`;

export const PointerEventWrapper = styled.div`
    pointer-events: all;
`;
